export const MAPPING_FIELDS = [
  {
    key: "networkId",
    label: "Network ID",
    aliases: ["networkid", "network_id"],
  },
  {
    key: "publisherDomain",
    label: "Publisher Domain / Referrer",
    aliases: [
      "publisherdomain",
      "publisher_domain",
      "referrer",
      "domain",
      "site_domain",
      "inventory_domain",
    ],
  },
  {
    key: "appDomain",
    label: "App Domain",
    aliases: ["appdomain", "app_domain"],
  },
  {
    key: "appBundle",
    label: "App Bundle",
    aliases: [
      "appbundle",
      "app_bundle",
      "output_bundle_id",
      "bundle",
      "bundle_id",
      "appid",
      "app_id",
    ],
  },
  {
    key: "expectedSellerId",
    label: "Expected Seller ID(s)",
    aliases: [
      "expectedsellerid",
      "expected_seller_id",
      "seller_id",
      "sellerid",
      "sid",
      "to_varchar(sid)",
    ],
  },
  {
    key: "expectedAdSystem",
    label: "Expected Ad System(s)",
    aliases: [
      "expectedadsystem",
      "expected_ad_system",
      "ad_system",
      "adsystem",
      "asi",
      "to_varchar(asi)",
    ],
  },
  {
    key: "sellerDomain",
    label: "Inventory Partner Domain",
    aliases: [
      "inventorypartnerdomain",
      "inventory_partner_domain",
      "sellerdomain",
      "seller_domain",
      "partner_domain",
    ],
  },
  {
    key: "publisherId",
    label: "Publisher ID",
    aliases: ["publisherid", "publisher_id", "pub_id", "pid", "site_publisher_id", "app_publisher_id"],
  },
  {
    key: "expectedRelationship",
    label: "Expected Relationship",
    aliases: ["relationship", "expectedrelationship", "seller_relationship"],
  },
];

function cleanText(value) {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value).replace(/\uFEFF/g, "").trim();
}

function normalizeHeader(header) {
  return cleanText(header).toLowerCase().replace(/[^a-z0-9]/g, "");
}

const KNOWN_HEADER_ALIASES = new Set(
  MAPPING_FIELDS.flatMap((field) => [field.key, ...field.aliases]).map((value) => normalizeHeader(value)),
);

function splitLines(value) {
  return cleanText(value)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

export function normalizeDomain(value) {
  const text = cleanText(value).toLowerCase();
  if (!text) {
    return "";
  }

  const withoutProtocol = text.replace(/^https?:\/\//, "").replace(/^www\./, "");
  const host = withoutProtocol.split("/")[0].split("?")[0].split("#")[0];
  return host.replace(/\.$/, "");
}

export function detectDelimiter(text) {
  const sample = splitLines(text).slice(0, 5).join("\n");
  const candidates = ["\t", ",", ";", "|"];
  let bestMatch = "";
  let bestScore = 0;

  candidates.forEach((candidate) => {
    const score = (sample.match(new RegExp(candidate === "\t" ? "\\t" : `\\${candidate}`, "g")) || []).length;
    if (score > bestScore) {
      bestScore = score;
      bestMatch = candidate;
    }
  });

  return bestScore > 0 ? bestMatch : "";
}

export function parseDelimitedLine(line, delimiter) {
  const cells = [];
  let current = "";
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];

    if (character === '"') {
      if (inQuotes && line[index + 1] === '"') {
        current += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (character === delimiter && !inQuotes) {
      cells.push(current.trim());
      current = "";
      continue;
    }

    current += character;
  }

  cells.push(current.trim());
  return cells;
}

function looksLikeDataValue(value) {
  const text = cleanText(value);
  if (!text) {
    return false;
  }

  const normalized = text.toLowerCase();

  if (/^https?:\/\//.test(normalized)) {
    return true;
  }

  if (/^[a-z0-9.-]+\.[a-z]{2,}(\/.*)?$/i.test(text)) {
    return true;
  }

  if (/^\d+([./:-]\d+)*$/.test(text)) {
    return true;
  }

  if (/^[a-z0-9._-]+\.[a-z0-9._-]+$/i.test(text)) {
    return true;
  }

  if (/^\[.*\]$/.test(text) || /^\{.*\}$/.test(text)) {
    return true;
  }

  return ["direct", "reseller", "publisher", "intermediary", "both"].includes(normalized);
}

function scoreHeaderCell(value) {
  const normalized = normalizeHeader(value);
  if (!normalized) {
    return 0;
  }

  if (KNOWN_HEADER_ALIASES.has(normalized)) {
    return 5;
  }

  if (/^(column|col)\d*$/.test(normalized)) {
    return 2;
  }

  if (!looksLikeDataValue(value) && /[a-z]/i.test(value)) {
    return 1;
  }

  return 0;
}

function inferHasHeader(lines, delimiter) {
  if (!Array.isArray(lines) || lines.length === 0) {
    return false;
  }

  const firstRow = parseDelimitedLine(lines[0], delimiter);
  const secondRow = lines[1] ? parseDelimitedLine(lines[1], delimiter) : [];
  const firstNonEmptyCount = firstRow.filter(Boolean).length;

  if (firstNonEmptyCount === 0) {
    return false;
  }

  const firstHeaderScore = firstRow.reduce((score, cell) => score + scoreHeaderCell(cell), 0);
  const secondHeaderScore = secondRow.reduce((score, cell) => score + scoreHeaderCell(cell), 0);
  const firstDataLikeCount = firstRow.filter(looksLikeDataValue).length;
  const secondDataLikeCount = secondRow.filter(looksLikeDataValue).length;

  if (firstHeaderScore >= Math.max(5, firstNonEmptyCount * 2)) {
    return true;
  }

  if (firstHeaderScore > secondHeaderScore && firstDataLikeCount < secondDataLikeCount) {
    return true;
  }

  return false;
}

function buildDefaultHeaders(count) {
  const defaults = MAPPING_FIELDS.map((field) => field.key);
  return Array.from({ length: count }, (_, index) => defaults[index] || `column_${index + 1}`);
}

function mapArrayRows(rows) {
  const maxColumns = rows.reduce((max, row) => Math.max(max, row.length), 0);
  const headers = buildDefaultHeaders(maxColumns);

  return {
    headers,
    rows: rows.map((row) => {
      const record = {};
      headers.forEach((header, index) => {
        record[header] = cleanText(row[index]);
      });
      return record;
    }),
  };
}

function mapObjectRows(rows) {
  const headers = [];

  rows.forEach((row) => {
    Object.keys(row || {}).forEach((key) => {
      if (!headers.includes(key)) {
        headers.push(key);
      }
    });
  });

  return {
    headers,
    rows: rows.map((row) => {
      const record = {};
      headers.forEach((header) => {
        record[header] = cleanText(row ? row[header] : "");
      });
      return record;
    }),
  };
}

function parseJsonInput(text) {
  const parsed = JSON.parse(text);

  if (Array.isArray(parsed)) {
    if (parsed.length === 0) {
      throw new Error("The JSON array is empty.");
    }

    if (Array.isArray(parsed[0])) {
      return { ...mapArrayRows(parsed), sourceType: "json-array" };
    }

    if (typeof parsed[0] === "object" && parsed[0] !== null) {
      return { ...mapObjectRows(parsed), sourceType: "json-array" };
    }
  }

  if (parsed && typeof parsed === "object") {
    const rows = Array.isArray(parsed.rows)
      ? parsed.rows
      : Array.isArray(parsed.data)
        ? parsed.data
        : Array.isArray(parsed.records)
          ? parsed.records
          : null;

    if (rows && rows.length > 0) {
      if (Array.isArray(rows[0])) {
        return { ...mapArrayRows(rows), sourceType: "json-object" };
      }

      if (typeof rows[0] === "object" && rows[0] !== null) {
        return { ...mapObjectRows(rows), sourceType: "json-object" };
      }
    }
  }

  throw new Error("JSON input must be an array of rows or an object containing a rows array.");
}

export function parseInput(rawText, delimiterPreference) {
  const cleaned = cleanText(rawText);

  if (!cleaned) {
    throw new Error("Paste or upload a dataset before analysing it.");
  }

  if (cleaned.startsWith("{") || cleaned.startsWith("[")) {
    return parseJsonInput(cleaned);
  }

  const lines = splitLines(cleaned);
  if (lines.length === 0) {
    throw new Error("No rows were detected in the provided input.");
  }

  const chosenDelimiter = delimiterPreference === "auto" ? detectDelimiter(cleaned) : delimiterPreference;

  if (!chosenDelimiter) {
    return {
      headers: ["publisherDomain"],
      rows: lines.map((line) => ({ publisherDomain: line })),
      sourceType: "line-list",
    };
  }

  const firstRow = parseDelimitedLine(lines[0], chosenDelimiter);
  const hasHeader = inferHasHeader(lines, chosenDelimiter);

  const headers = hasHeader ? firstRow.map((cell, index) => cleanText(cell) || `column_${index + 1}`) : buildDefaultHeaders(firstRow.length);
  const dataLines = hasHeader ? lines.slice(1) : lines;

  const rows = dataLines.map((line) => {
    const values = parseDelimitedLine(line, chosenDelimiter);
    const record = {};
    headers.forEach((header, index) => {
      record[header] = cleanText(values[index]);
    });
    return record;
  });

  return {
    headers,
    rows,
    sourceType: chosenDelimiter === "\t" ? "tsv" : "delimited",
  };
}

export function guessMapping(headers) {
  const mapping = {};

  MAPPING_FIELDS.forEach((field) => {
    const normalizedAliases = field.aliases.map((alias) => normalizeHeader(alias));
    const headerMatch = headers.find((header) => normalizedAliases.includes(normalizeHeader(header)));
    if (headerMatch) {
      mapping[field.key] = headerMatch;
    }
  });

  return mapping;
}

export function buildNormalizedRows(parsedInput, mapping) {
  if (!parsedInput) {
    return [];
  }

  return parsedInput.rows
    .map((row, index) => {
      const normalized = { sourceRowNumber: index + 1 };

      MAPPING_FIELDS.forEach((field) => {
        const sourceKey = mapping[field.key];
        normalized[field.key] = sourceKey ? cleanText(row[sourceKey]) : "";
      });

      return normalized;
    })
    .filter((row) =>
      [
        row.publisherDomain,
        row.appDomain,
        row.appBundle,
        row.expectedSellerId,
        row.expectedAdSystem,
        row.sellerDomain,
      ].some(Boolean),
    );
}

export function countSeverities(issues) {
  const summary = { error: 0, warning: 0, info: 0 };

  (issues || []).forEach((issue) => {
    const severity = issue && issue.severity ? issue.severity : "info";
    if (summary[severity] === undefined) {
      summary[severity] = 0;
    }
    summary[severity] += 1;
  });

  return summary;
}

export function resultIssueCount(result) {
  const summary = result && result.summary ? result.summary : {};
  return Number(summary.error || 0) + Number(summary.warning || 0) + Number(summary.info || 0);
}

export function computeHealthScore(result) {
  const nonInformationalEdges = ((result && result.supplyChain && result.supplyChain.edges) || []).filter(
    (edge) => !edge.informational,
  );

  if (nonInformationalEdges.length === 0) {
    return result && result.supplyChain && result.supplyChain.status === "ok" ? 100 : 0;
  }

  const blockingEdges = Number(result && result.supplyChain ? result.supplyChain.edgeErrors || 0 : 0);
  const issuePenalty = Math.min(resultIssueCount(result) * 4, 24);
  const structuralScore = ((nonInformationalEdges.length - blockingEdges) / nonInformationalEdges.length) * 100;
  return Math.max(0, Math.round(structuralScore - issuePenalty));
}

export function buildChainText(result) {
  const nodes = (result && result.supplyChain && result.supplyChain.nodes) || [];
  return nodes
    .map((node) => node.label || node.subtitle || node.kind || "")
    .filter(Boolean)
    .join(" -> ");
}

export function filterResults(results, resultFilter, searchQuery) {
  const loweredQuery = cleanText(searchQuery).toLowerCase();

  return (results || [])
    .map((result, index) => ({ result, index }))
    .filter(({ result }) => {
      if (resultFilter === "broken" && result.supplyChain && result.supplyChain.status === "ok") {
        return false;
      }

      if (resultFilter === "healthy" && (!result.supplyChain || result.supplyChain.status !== "ok")) {
        return false;
      }

      if (!loweredQuery) {
        return true;
      }

      const haystack = [
        result.targetDomain,
        buildChainText(result),
        result.input && result.input.publisherDomain,
        result.input && result.input.appBundle,
        result.input && result.input.expectedSellerId,
        result.input && result.input.expectedAdSystem,
        ...((result.issues || []).map((issue) => `${issue.code || ""} ${issue.message || ""}`)),
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(loweredQuery);
    });
}

function escapeCsv(value) {
  const text = cleanText(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

export function downloadBlob(filename, contents, type) {
  const blob = new Blob([contents], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function exportResultsJson(resultsPayload) {
  downloadBlob(
    `schain-results-${new Date().toISOString().replace(/:/g, "-")}.json`,
    JSON.stringify(resultsPayload, null, 2),
    "application/json",
  );
}

export function exportResultsCsv(results) {
  const rows = [
    [
      "source_row",
      "inventory",
      "chain_status",
      "chain_flow",
      "severity",
      "code",
      "message",
      "node_count",
      "broken_upstream_links",
      "publisher_domain",
      "app_bundle",
      "network_id",
      "publisher_id",
      "seller_ids",
      "ad_systems",
    ],
  ];

  (results || []).forEach((result) => {
    const input = result.input || {};
    const sellerIds = (result.matchedPairs || [])
      .map((pair) => pair.sellerId || "")
      .filter(Boolean)
      .join(" | ");
    const adSystems = (result.matchedPairs || [])
      .map((pair) => pair.adSystem || "")
      .filter(Boolean)
      .join(" | ");

    const baseRow = [
      input.sourceRowNumber || "",
      result.targetDomain || "",
      result.supplyChain ? result.supplyChain.status || "" : "",
      buildChainText(result),
      "",
      "",
      "",
      (result.matchedPairs || []).length,
      result.supplyChain ? result.supplyChain.edgeErrors || 0 : 0,
      input.publisherDomain || "",
      input.appBundle || "",
      input.networkId || "",
      input.publisherId || "",
      sellerIds,
      adSystems,
    ];

    if (Array.isArray(result.issues) && result.issues.length > 0) {
      result.issues.forEach((issue) => {
        const issueRow = baseRow.slice();
        issueRow[4] = issue.severity || "";
        issueRow[5] = issue.code || "";
        issueRow[6] = issue.message || "";
        rows.push(issueRow);
      });
    } else {
      const cleanRow = baseRow.slice();
      cleanRow[4] = "info";
      cleanRow[5] = "NO_ISSUES";
      cleanRow[6] = "No issues were found for this row.";
      rows.push(cleanRow);
    }
  });

  const csv = rows.map((row) => row.map((value) => escapeCsv(value)).join(",")).join("\r\n");
  downloadBlob(
    `schain-results-${new Date().toISOString().replace(/:/g, "-")}.csv`,
    csv,
    "text/csv;charset=utf-8",
  );
}
